import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Cell } from "recharts";
import { BarChart3 } from "lucide-react";

export function Sparkline({ points, peak }) {
  if (!points?.length) return null;
  const w = 118, h = 26, max = Math.max(peak || 0, 1);
  const n = points.length;
  const step = w / (n - 1 || 1);
  const coords = points.map((p, i) => [i * step, h - (p.peak / max) * (h - 5) - 2]);
  const line = coords.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const area = `${line} L${w.toFixed(1)} ${h} L0 ${h} Z`;
  const [lx, ly] = coords[coords.length - 1];
  const bw = w / n;
  const fmt = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric" });
  return (
    <div className="flex items-center gap-2 pl-1" data-testid="presence-sparkline">
      <svg width={w} height={h} className="shrink-0">
        <path d={area} fill="#10b98122" />
        <path d={line} fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={lx} cy={ly} r="2.5" fill="#10b981" />
        {points.map((p, i) => (
          <rect key={i} data-testid={`sparkline-hour-${i}`} x={(i * step - bw / 2).toFixed(1)} y="0" width={bw.toFixed(1)} height={h} fill="transparent" style={{ cursor: "pointer" }}>
            <title>{`${p.peak} online · ${fmt(p.hour)}`}</title>
          </rect>
        ))}
      </svg>
      <span className="text-[11px] text-slate-500 whitespace-nowrap leading-tight" title={`Peak ${peak} user(s) online in the last 24 hours`}>24h online<br />peak {peak}</span>
    </div>
  );
}

export const hourLabel = (h) => { const d = new Date(); d.setHours(h, 0, 0, 0); return d.toLocaleTimeString([], { hour: "numeric" }); };

export function PresenceAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/presence/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="p-5 text-sm text-slate-400" data-testid="presence-analytics-loading">Loading presence analytics…</div>;
  const hours = data.busiest_hours.map((b) => ({ label: hourLabel(b.hour), hour: b.hour, avg: b.avg }));
  const maxAvg = Math.max(...hours.map((h) => h.avg), 0);
  const days = data.daily_peaks.map((d) => ({ label: new Date(d.date + "T12:00:00").toLocaleDateString([], { weekday: "short" }), date: d.date, peak: d.peak }));
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-3" data-testid="presence-analytics">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-4">
        <h3 className="text-sm font-bold text-[#12234A] flex items-center gap-1.5"><BarChart3 size={15} className="text-emerald-600" /> Presence analytics <span className="font-normal text-slate-400">· last 7 days</span></h3>
        <span className="text-xs text-slate-500" data-testid="analytics-week-peak">Peak concurrent this week: <b className="text-[#12234A]">{data.week_peak}</b></span>
        <span className="text-xs text-slate-500" data-testid="analytics-busiest-hour">Busiest hour: <b className="text-[#12234A]">{data.busiest_hour != null ? hourLabel(data.busiest_hour) : "—"}</b></span>
        {!data.has_data && <span className="text-xs text-amber-500">Collecting data — charts fill in as people come online.</span>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Busiest hours ({data.tz?.split("/")[1]?.replace("_", " ") || "local"})</p>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={hours} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#94a3b8" }} interval={2} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <RTooltip cursor={{ fill: "#10b98111" }} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} formatter={(v) => [`${v} avg online`, "Avg"]} labelFormatter={(l) => `${l}`} />
              <Bar dataKey="avg" radius={[3, 3, 0, 0]}>
                {hours.map((h, i) => <Cell key={i} fill={maxAvg > 0 && h.avg === maxAvg ? "#059669" : "#6ee7b7"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Peak concurrent users / day</p>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={days} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <RTooltip cursor={{ fill: "#12234A0d" }} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} formatter={(v) => [`${v} peak`, "Concurrent"]} />
              <Bar dataKey="peak" radius={[3, 3, 0, 0]} fill="#12234A" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
